import React, { useEffect, useRef, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Package, ImageOff } from "lucide-react";

// Card that lazy-loads its image via IntersectionObserver once it scrolls into view.
// Keeps the initial /admin/photos list response tiny (metadata only) and progressively
// hydrates full-size base64 images from /admin/photos/{id} as the admin scrolls.
const AdminPhotoCard = ({ p, onDelete, onView }) => {
  const cardRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | loaded | error

  useEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        io.disconnect();
        setState("loading");
        try {
          const r = await api.get(`/admin/photos/${p.id}`);
          setImgSrc(r.data.image_url);
          setState("loaded");
        } catch {
          setState("error");
        }
      },
      { rootMargin: "200px" } // start loading a bit before it enters the viewport
    );
    io.observe(node);
    return () => io.disconnect();
  }, [p.id]);

  return (
    <div ref={cardRef} className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid={`admin-photo-${p.id}`}>
      <button
        onClick={() => imgSrc && onView({ ...p, image_url: imgSrc })}
        className="w-full h-40 bg-[#EBF4F6] flex items-center justify-center overflow-hidden"
        data-testid={`admin-photo-view-${p.id}`}
        disabled={state !== "loaded"}
      >
        {state === "loaded" && imgSrc ? (
          <img
            src={imgSrc}
            alt={p.display_name || ""}
            className="w-full h-full object-cover cursor-zoom-in hover:scale-105 transition-transform"
          />
        ) : state === "error" ? (
          <div className="flex flex-col items-center gap-1 text-red-500">
            <ImageOff size={28} />
            <span className="text-xs font-bold">تعذّر التحميل</span>
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#EBF4F6] to-[#7AB2B2]/30 animate-pulse" />
        )}
      </button>
      <div className="p-3 space-y-1">
        <div className="font-bold text-[#333333] text-sm truncate">{p.merchant_name}</div>
        <div className="text-xs text-[#666666]">{p.category} · {p.type}</div>
        <div className="text-xs text-[#666666]">{p.size} · {p.color || "—"}</div>
        <div className="text-xs text-[#333333]" data-testid={`admin-photo-code-${p.id}`}>
          كود المنتج: <span dir="ltr" className="font-mono font-bold text-[#09637E]">{p.product_code || "—"}</span>
        </div>
        <div className="text-[#09637E] font-bold" dir="ltr">${(p.price_usd ?? p.price ?? 0).toFixed(2)} / م</div>
        <button onClick={() => onDelete(p.id)} data-testid={`admin-delete-photo-${p.id}`}
          className="w-full mt-2 h-9 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center justify-center gap-1">
          <Trash2 size={14} /> حذف
        </button>
      </div>
    </div>
  );
};

const AdminPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewPhoto, setViewPhoto] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/photos");
      setPhotos(r.data);
    } catch (e) { toast.error(arabicError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الصورة؟")) return;
    try { await api.delete(`/admin/photos/${id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(arabicError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-photos-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">جميع الصور</h1>
        <p className="text-[#666666] mt-1">إجمالي: {photos.length} صورة — اضغط الصورة للتكبير</p>
      </div>

      {loading ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>
      ) : photos.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center">
          <Package className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] text-lg">لا توجد صور بعد</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <AdminPhotoCard key={p.id} p={p} onDelete={del} onView={setViewPhoto} />
          ))}
        </div>
      )}

      {viewPhoto && viewPhoto.image_url && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setViewPhoto(null)}
          data-testid="admin-photo-lightbox"
        >
          <button
            onClick={() => setViewPhoto(null)}
            className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[#333333] font-extrabold text-xl"
            aria-label="إغلاق"
          >✕</button>
          <img
            src={viewPhoto.image_url}
            alt={viewPhoto.display_name || ""}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 inset-x-4 text-center text-white text-sm md:text-base font-bold bg-black/50 rounded-lg py-2 px-4 max-w-2xl mx-auto">
            <div>{viewPhoto.merchant_name} — {viewPhoto.display_name}</div>
            <div className="text-xs mt-1 opacity-80">
              {viewPhoto.category} · {viewPhoto.type} · {viewPhoto.country} · {viewPhoto.size}
              {viewPhoto.color ? ` · ${viewPhoto.color}` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPhotos;
